import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomerLineFilterToolbox from "@/components/CustomerLineFilterToolbox.jsx";
import AdminCustomerLineLabel from "@/components/AdminCustomerLineLabel.jsx";
import Price from "@/components/Price.jsx";
import { searchAdminLikedItems } from "@/api/admin.api.js";
import { getFileUrl } from "@/api/public.api.js";
import { EMPTY_CUSTOMER_LINE_FILTERS } from "@/utils/customerLineFilters.js";
import "@/components/product-filter.css";

function formatAddedDate(value) {
    if (!value) {
        return "—";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString("fr-FR");
}

function productImageUrl(photoId) {
    return photoId ? getFileUrl(photoId) : null;
}

/** Group flat liked lines by customer; first-seen order preserves newest-first API ranking. */
function groupLinesByCustomer(lines) {
    const groups = [];
    const indexByUsername = new Map();
    for (const line of lines) {
        const key = line.username || "";
        let group = indexByUsername.get(key);
        if (!group) {
            group = {
                username: line.username,
                customerDisplayName: line.customerDisplayName,
                lines: []
            };
            indexByUsername.set(key, group);
            groups.push(group);
        }
        group.lines.push(line);
    }
    return groups;
}

export default function AdminVoeuxSection({ setMessage, setError }) {
    const [draftFilters, setDraftFilters] = useState(EMPTY_CUSTOMER_LINE_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_CUSTOMER_LINE_FILTERS);
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(false);
    const loadRequestIdRef = useRef(0);

    const load = useCallback(async (filters) => {
        const requestId = ++loadRequestIdRef.current;
        setLoading(true);
        setError("");
        setMessage("");
        try {
            const response = await searchAdminLikedItems({
                customer: filters.customer.trim() || undefined,
                search: filters.search.trim() || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                page: 0,
                size: 50
            });
            if (requestId !== loadRequestIdRef.current) {
                return;
            }
            setLines(response.data || []);
        } catch (err) {
            if (requestId !== loadRequestIdRef.current) {
                return;
            }
            setError(err.message);
            setLines([]);
        } finally {
            if (requestId === loadRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [setError, setMessage]);

    useEffect(() => {
        load(appliedFilters);
    }, [load, appliedFilters]);

    const customerGroups = useMemo(() => groupLinesByCustomer(lines), [lines]);

    const applyFilters = async (nextFilters) => {
        const filtersToApply = nextFilters ?? draftFilters;
        if (nextFilters) {
            setDraftFilters(nextFilters);
        }
        setAppliedFilters({ ...filtersToApply });
    };

    const resetFilters = async () => {
        setDraftFilters(EMPTY_CUSTOMER_LINE_FILTERS);
        setAppliedFilters(EMPTY_CUSTOMER_LINE_FILTERS);
    };

    return (
        <section className="card profile-section profile-section--catalog">
            <div className="catalog-with-filters">
                <CustomerLineFilterToolbox
                    filters={draftFilters}
                    loading={loading}
                    onChange={setDraftFilters}
                    onApply={applyFilters}
                    onReset={resetFilters}
                />

                <div className="catalog-results">
                    <div className="catalog-results-header">
                        <h2>Gérer les « J&apos;aime »</h2>
                        <p className="muted catalog-results-count admin-list-hint">
                            Consultez les produits dans « J&apos;aime » des clients (lecture seule). Les plus récemment ajoutés apparaissent en premier.
                        </p>
                    </div>

                    {loading && <p className="muted">Chargement des J&apos;aime...</p>}
                    {!loading && lines.length === 0 && (
                        <p className="muted">Aucun produit dans « J&apos;aime » ne correspond à votre recherche.</p>
                    )}

                    {!loading && customerGroups.length > 0 && (
                        <div className="admin-table purchase-list">
                            {customerGroups.map((group) => (
                                <div
                                    key={group.username || group.customerDisplayName}
                                    className="admin-row admin-list-row purchase-list-row admin-cart-line-row admin-liked-customer-card"
                                >
                                    <div className="admin-list-row-main purchase-list-row-main purchase-list-row-main--static">
                                        <AdminCustomerLineLabel
                                            displayName={group.customerDisplayName}
                                            username={group.username}
                                        />
                                        {group.lines.map((line) => (
                                            <div key={line.id} className="admin-liked-product-block">
                                                <div className="purchase-list-row-top">
                                                    {productImageUrl(line.photoId) ? (
                                                        <img
                                                            src={productImageUrl(line.photoId)}
                                                            alt=""
                                                            className="purchase-row-thumb"
                                                        />
                                                    ) : (
                                                        <div className="purchase-row-thumb purchase-row-thumb--empty" aria-hidden="true" />
                                                    )}
                                                    <div className="purchase-list-row-heading">
                                                        <strong>{line.title}</strong>
                                                        {line.category && <p className="muted">{line.category}</p>}
                                                        <p><Price value={line.unitPrice} /></p>
                                                    </div>
                                                </div>
                                                <p className="muted purchase-row-date">
                                                    Ajouté le {formatAddedDate(line.addedAt)}
                                                    {line.expiresAt ? ` · expire le ${formatAddedDate(line.expiresAt)}` : ""}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
